/**
 * Feature Keys Registry - Source of truth centralizado
 *
 * Todas as feature keys da plataforma organizadas por categoria.
 * Usado pelo backend (featureGate, featureService, seed) e frontend (PlansPage, AuthContext).
 *
 * Cada feature define:
 *   - key: identificador único (formato: categoria.recurso)
 *   - label: nome amigável (pt-BR)
 *   - category: categoria de agrupamento
 *   - defaultLimitType: tipo padrão (boolean, number, unlimited)
 *   - description: descrição curta
 */

export const FEATURE_CATEGORIES = {
  users: { label: 'Usuários', icon: 'Users', order: 1 },
  contact: { label: 'Contatos', icon: 'Contact', order: 2 },
  channels: { label: 'Canais', icon: 'MessageSquare', order: 3 },
  inbox: { label: 'Inbox', icon: 'Inbox', order: 4 },
  flows: { label: 'Fluxos', icon: 'GitBranch', order: 5 },
  ai: { label: 'Inteligência Artificial', icon: 'Bot', order: 6 },
  integrations: { label: 'Integrações', icon: 'Plug', order: 7 },
  reports: { label: 'Relatórios', icon: 'BarChart3', order: 8 },
  automations: { label: 'Automações', icon: 'Clock', order: 9 },
  rag: { label: 'Base de Conhecimento', icon: 'BookOpen', order: 10 },
  webhooks: { label: 'Webhooks', icon: 'Webhook', order: 11 },
  calling: { label: 'Chamadas', icon: 'Phone', order: 12 },
  sales: { label: 'Sales Intelligence', icon: 'TrendingUp', order: 13 },
  crm: { label: 'CRM / Kanban', icon: 'Kanban', order: 14 },
  ads: { label: 'Ads Intelligence', icon: 'Megaphone', order: 15 },
  embed: { label: 'Interface Embarcável', icon: 'AppWindow', order: 16 },
};

/**
 * Chave única do recurso "Touch App" (app do cliente final).
 * Importada pelo featureService, pelo tcAddonGate e pelo router de canal —
 * literais soltos foram o que tornou a renomeação anterior um trabalho manual.
 */
export const APP_CONNECT_FEATURE_KEY = 'channels.app_connect';

/** Limite de contatos ativos da conta. Não representa usuários internos. */
export const CONTACT_LIMIT_FEATURE_KEY = 'contact.max';

/**
 * Chave do entitlement de "conta parceira" da interface embarcável (embed).
 * Concedido por conta via `ClientAccount.customLimits`, não por tier de plano —
 * é o segundo dos dois trancos independentes exigidos pela API externa de
 * onboarding/embed: escopo no ApiToken E esta feature na conta (AD-001, ver
 * .specs/features/embeddable-interface/design.md).
 */
export const EMBED_PARTNER_FEATURE_KEY = 'embed.partner';

export const FEATURE_KEYS = [
  // ═══════════════════════════════════════
  // USUÁRIOS
  // ═══════════════════════════════════════
  { key: 'users.max', label: 'Máx. usuários', category: 'users', defaultLimitType: 'number', description: 'Limite de usuários na conta', addonPrice: null, addonUnitSize: 1 },
  { key: 'users.agents_max', label: 'Máx. agentes', category: 'users', defaultLimitType: 'number', description: 'Limite de agentes de atendimento', addonPrice: null },

  // ═══════════════════════════════════════
  // CONTATOS
  // ═══════════════════════════════════════
  { key: CONTACT_LIMIT_FEATURE_KEY, label: 'Máx. contatos', category: 'contact', defaultLimitType: 'unlimited', description: 'Quantidade máxima de contatos ativos na conta', addonPrice: null, addonUnitSize: null },

  // ═══════════════════════════════════════
  // CANAIS
  // ═══════════════════════════════════════
  { key: 'channels.whatsapp', label: 'WhatsApp', category: 'channels', defaultLimitType: 'boolean', description: 'Habilitar canal WhatsApp', addonPrice: null },
  { key: 'channels.whatsapp_numbers', label: 'Números WhatsApp', category: 'channels', defaultLimitType: 'number', description: 'Quantidade de números WhatsApp', addonPrice: 49.90 },
  { key: APP_CONNECT_FEATURE_KEY, label: 'Touch App', category: 'channels', defaultLimitType: 'boolean', description: 'Habilitar o app Touch Connect para os clientes do escritório', addonPrice: null },
  { key: 'channels.instagram', label: 'Instagram', category: 'channels', defaultLimitType: 'boolean', description: 'Habilitar canal Instagram', addonPrice: 39.90 },
  { key: 'channels.instagram_accounts', label: 'Contas Instagram', category: 'channels', defaultLimitType: 'number', description: 'Quantidade de contas Instagram', addonPrice: null },
  { key: 'channels.tiktok', label: 'TikTok', category: 'channels', defaultLimitType: 'boolean', description: 'Habilitar canal TikTok', addonPrice: 39.90 },
  { key: 'channels.tiktok_accounts', label: 'Contas TikTok', category: 'channels', defaultLimitType: 'number', description: 'Quantidade de contas TikTok', addonPrice: null },
  { key: 'channels.email', label: 'E-mail', category: 'channels', defaultLimitType: 'boolean', description: 'Habilitar canal de E-mail', addonPrice: 29.90 },
  { key: 'channels.email_accounts', label: 'Contas E-mail', category: 'channels', defaultLimitType: 'number', description: 'Quantidade de contas de E-mail', addonPrice: null },
  { key: 'channels.telegram', label: 'Telegram', category: 'channels', defaultLimitType: 'boolean', description: 'Habilitar canal Telegram', addonPrice: 29.90 },
  { key: 'channels.telegram_accounts', label: 'Contas Telegram', category: 'channels', defaultLimitType: 'number', description: 'Quantidade de contas Telegram', addonPrice: null },
  { key: 'channels.webchat', label: 'Webchat', category: 'channels', defaultLimitType: 'boolean', description: 'Habilitar canal Webchat', addonPrice: null },
  { key: 'channels.webchat_accounts', label: 'Contas Webchat', category: 'channels', defaultLimitType: 'number', description: 'Quantidade de contas Webchat', addonPrice: null },

  // ═══════════════════════════════════════
  // INBOX
  // ═══════════════════════════════════════
  { key: 'inbox.enabled', label: 'Inbox habilitado', category: 'inbox', defaultLimitType: 'boolean', description: 'Acesso ao módulo de Inbox', addonPrice: null },
  { key: 'inbox.tickets_monthly', label: 'Tickets mensais', category: 'inbox', defaultLimitType: 'number', description: 'Limite de tickets por mês', addonPrice: null },
  { key: 'inbox.auto_distribution', label: 'Distribuição automática', category: 'inbox', defaultLimitType: 'boolean', description: 'Distribuição automática de tickets', addonPrice: 29.90 },
  { key: 'inbox.sla', label: 'SLA', category: 'inbox', defaultLimitType: 'boolean', description: 'Controle de SLA nos atendimentos', addonPrice: 39.90 },
  { key: 'inbox.groups', label: 'Grupos', category: 'inbox', defaultLimitType: 'boolean', description: 'Habilitar grupos de atendimento', addonPrice: 199 },
  { key: 'inbox.calls', label: 'Ligações', category: 'inbox', defaultLimitType: 'boolean', description: 'Habilitar ligações na inbox', addonPrice: 49.90 },
  { key: 'inbox.csat', label: 'CSAT', category: 'inbox', defaultLimitType: 'boolean', description: 'Pesquisa de satisfação (CSAT)', addonPrice: 29.90 },
  { key: 'inbox.nps', label: 'NPS', category: 'inbox', defaultLimitType: 'boolean', description: 'Pesquisa NPS', addonPrice: 29.90 },

  // ═══════════════════════════════════════
  // FLUXOS
  // ═══════════════════════════════════════
  { key: 'flows.max', label: 'Máx. fluxos', category: 'flows', defaultLimitType: 'number', description: 'Limite de fluxos de automação', addonPrice: null },
  { key: 'flows.nodes_per_flow', label: 'Nós por fluxo', category: 'flows', defaultLimitType: 'number', description: 'Limite de nós por fluxo', addonPrice: null },
  { key: 'flows.versions', label: 'Versões por fluxo', category: 'flows', defaultLimitType: 'number', description: 'Limite de versões salvas por fluxo', addonPrice: null },

  // ═══════════════════════════════════════
  // INTELIGÊNCIA ARTIFICIAL
  // ═══════════════════════════════════════
  { key: 'ai.enabled', label: 'IA habilitada', category: 'ai', defaultLimitType: 'boolean', description: 'Acesso às funcionalidades de IA', addonPrice: null },
  { key: 'ai.agent_calls', label: 'Chamadas IA/mês', category: 'ai', defaultLimitType: 'number', description: 'Requisições ao agente de IA por mês', addonPrice: null },
  { key: 'ai.tokens_monthly', label: 'Tokens mensais', category: 'ai', defaultLimitType: 'number', description: 'Limite de tokens de IA por mês', addonPrice: null },
  { key: 'ai.classifier', label: 'Classificador', category: 'ai', defaultLimitType: 'boolean', description: 'Classificador automático por IA', addonPrice: 29.90 },
  { key: 'ai.extractor', label: 'Extrator', category: 'ai', defaultLimitType: 'boolean', description: 'Extração de dados por IA', addonPrice: 29.90 },
  { key: 'ai.summarizer', label: 'Resumidor', category: 'ai', defaultLimitType: 'boolean', description: 'Resumo automático de conversas', addonPrice: 19.90 },
  { key: 'ai.premium_models', label: 'Modelos premium (Claude)', category: 'ai', defaultLimitType: 'boolean', description: 'Habilita Anthropic Claude (Haiku/Sonnet/Opus) nos agentes. Por padrão usa-se Grok 4.1 Fast (~10× mais barato).', addonPrice: 99.90 },

  // ═══════════════════════════════════════
  // INTEGRAÇÕES
  // ═══════════════════════════════════════
  { key: 'integrations.google_calendar', label: 'Google Calendar', category: 'integrations', defaultLimitType: 'boolean', description: 'Integração com Google Calendar', addonPrice: null },
  { key: 'integrations.google_mail', label: 'Google Mail', category: 'integrations', defaultLimitType: 'boolean', description: 'Integração com Google Mail', addonPrice: 19.90 },
  { key: 'integrations.asaas', label: 'Asaas', category: 'integrations', defaultLimitType: 'boolean', description: 'Integração financeira Asaas', addonPrice: 29.90 },
  { key: 'integrations.advbox', label: 'AdvBox', category: 'integrations', defaultLimitType: 'boolean', description: 'Integração jurídica AdvBox', addonPrice: 49.90 },
  { key: 'integrations.projuris', label: 'Projuris', category: 'integrations', defaultLimitType: 'boolean', description: 'Integração jurídica Projuris', addonPrice: 49.90 },
  { key: 'integrations.pipedrive', label: 'Pipedrive', category: 'integrations', defaultLimitType: 'boolean', description: 'Integração CRM Pipedrive', addonPrice: 39.90 },
  { key: 'integrations.rdstation', label: 'RD Station', category: 'integrations', defaultLimitType: 'boolean', description: 'Integração marketing RD Station', addonPrice: 39.90 },
  { key: 'integrations.trello', label: 'Trello', category: 'integrations', defaultLimitType: 'boolean', description: 'Kanban Trello integrado à plataforma', addonPrice: 29.90 },
  { key: 'integrations.faz', label: 'SEDEP FAZ', category: 'integrations', defaultLimitType: 'boolean', description: 'Integração jurídica SEDEP FAZ', addonPrice: 49.90 },
  { key: 'integrations.controljus', label: 'ControlJUS', category: 'integrations', defaultLimitType: 'boolean', description: 'Integração jurídica ControlJUS', addonPrice: 49.90 },

  // ═══════════════════════════════════════
  // INTERFACE EMBARCÁVEL (EMBED)
  // ═══════════════════════════════════════
  { key: EMBED_PARTNER_FEATURE_KEY, label: 'Conta parceira (embed)', category: 'embed', defaultLimitType: 'boolean', description: 'Habilita a API de onboarding, o login em segundo plano e a interface embarcável para esta conta', addonPrice: null },

  // ═══════════════════════════════════════
  // RELATÓRIOS
  // ═══════════════════════════════════════
  { key: 'reports.basic', label: 'Relatórios básicos', category: 'reports', defaultLimitType: 'boolean', description: 'Relatórios essenciais', addonPrice: null },
  { key: 'reports.advanced', label: 'Relatórios avançados', category: 'reports', defaultLimitType: 'boolean', description: 'Relatórios detalhados e analytics', addonPrice: 49.90 },
  { key: 'reports.overview', label: 'Visão geral', category: 'reports', defaultLimitType: 'boolean', description: 'Dashboard de visão geral', addonPrice: null },
  { key: 'reports.agents', label: 'Agentes', category: 'reports', defaultLimitType: 'boolean', description: 'Relatório de performance de agentes', addonPrice: 39.90 },
  { key: 'reports.channels', label: 'Canais', category: 'reports', defaultLimitType: 'boolean', description: 'Relatório por canal de comunicação', addonPrice: 39.90 },
  { key: 'reports.satisfaction', label: 'Satisfação', category: 'reports', defaultLimitType: 'boolean', description: 'Relatório de satisfação do cliente', addonPrice: 39.90 },
  { key: 'reports.sla', label: 'SLA', category: 'reports', defaultLimitType: 'boolean', description: 'Relatório de cumprimento de SLA', addonPrice: 49.90 },
  { key: 'reports.insights', label: 'Insights IA', category: 'reports', defaultLimitType: 'boolean', description: 'Insights gerados por inteligência artificial', addonPrice: 59.90 },
  { key: 'reports.custom', label: 'Personalizados', category: 'reports', defaultLimitType: 'boolean', description: 'Relatórios personalizados pelo usuário', addonPrice: 59.90 },

  // ═══════════════════════════════════════
  // AUTOMAÇÕES (CRON JOBS)
  // ═══════════════════════════════════════
  { key: 'automations.enabled', label: 'Automações habilitadas', category: 'automations', defaultLimitType: 'boolean', description: 'Habilitar jobs agendados (cron)', addonPrice: 49.90 },
  { key: 'automations.scheduled_jobs_max', label: 'Máx. jobs agendados', category: 'automations', defaultLimitType: 'number', description: 'Limite de cron jobs ativos simultaneamente', addonPrice: null },
  { key: 'automations.executions_monthly', label: 'Execuções mensais', category: 'automations', defaultLimitType: 'number', description: 'Limite de acionamentos de automação por mês', addonPrice: null },
  { key: 'automations.advbox_pack_1000', label: 'Advbox 1.000 exec', category: 'automations', defaultLimitType: 'boolean', description: 'Pacote Advbox: 1.000 execuções/mês, 3 agendamentos', addonPrice: 149.90 },
  { key: 'automations.advbox_pack_5000', label: 'Advbox 5.000 exec', category: 'automations', defaultLimitType: 'boolean', description: 'Pacote Advbox: 5.000 execuções/mês, 10 agendamentos, PDF+GPT+Email', addonPrice: 299.90 },
  { key: 'automations.advbox_pack_15000', label: 'Advbox 15.000 exec', category: 'automations', defaultLimitType: 'boolean', description: 'Pacote Advbox: 15.000 execuções/mês, 25 agendamentos, PDF+GPT+Email', addonPrice: 599.90 },
  { key: 'automations.advbox_pack_50000', label: 'Advbox 50.000 exec', category: 'automations', defaultLimitType: 'boolean', description: 'Pacote Advbox: 50.000 execuções/mês, ilimitado agendamentos, PDF+GPT+Email', addonPrice: 999.90 },
  { key: 'automations.advbox_executions', label: 'Execuções Advbox', category: 'automations', defaultLimitType: 'number', description: 'Contador de execuções mensais Advbox (gerenciado automaticamente)', addonPrice: null },
  { key: 'automations.advbox_overage', label: 'Excedente Advbox', category: 'automations', defaultLimitType: 'number', description: 'Execuções excedentes Advbox (R$ 0,25/exec extra)', addonPrice: 0.25 },
  { key: 'automations.projuris_executions', label: 'Execuções Projuris', category: 'automations', defaultLimitType: 'number', description: 'Contador de execuções mensais Projuris (gerenciado automaticamente)', addonPrice: null },

  // ControlJUS — os limites de cada pacote são capacidade técnica; `addonPrice`
  // fica null até a definição comercial. A tela mostra "a definir" em vez de um
  // número inventado, e o pacote já pode ser concedido a uma conta enquanto o
  // preço não sai.
  { key: 'automations.controljus_pack_1000', label: 'ControlJUS 1.000 exec', category: 'automations', defaultLimitType: 'boolean', description: 'Pacote ControlJUS: 1.000 execuções/mês, 3 agendamentos', addonPrice: null },
  { key: 'automations.controljus_pack_5000', label: 'ControlJUS 5.000 exec', category: 'automations', defaultLimitType: 'boolean', description: 'Pacote ControlJUS: 5.000 execuções/mês, 10 agendamentos, PDF+GPT+Email', addonPrice: null },
  { key: 'automations.controljus_pack_15000', label: 'ControlJUS 15.000 exec', category: 'automations', defaultLimitType: 'boolean', description: 'Pacote ControlJUS: 15.000 execuções/mês, 25 agendamentos, PDF+GPT+Email', addonPrice: null },
  { key: 'automations.controljus_pack_50000', label: 'ControlJUS 50.000 exec', category: 'automations', defaultLimitType: 'boolean', description: 'Pacote ControlJUS: 50.000 execuções/mês, agendamentos ilimitados, PDF+GPT+Email', addonPrice: null },
  { key: 'automations.controljus_executions', label: 'Execuções ControlJUS', category: 'automations', defaultLimitType: 'number', description: 'Contador de execuções mensais ControlJUS (gerenciado automaticamente)', addonPrice: null },
  { key: 'automations.controljus_overage', label: 'Excedente ControlJUS', category: 'automations', defaultLimitType: 'number', description: 'Execuções excedentes ControlJUS (preço por execução a definir)', addonPrice: null },


  // ═══════════════════════════════════════
  // BASE DE CONHECIMENTO (RAG)
  // ═══════════════════════════════════════
  { key: 'rag.enabled', label: 'RAG habilitado', category: 'rag', defaultLimitType: 'boolean', description: 'Base de conhecimento com IA', addonPrice: 39.90 },
  { key: 'rag.documents_max', label: 'Máx. documentos', category: 'rag', defaultLimitType: 'number', description: 'Limite de documentos na base', addonPrice: null },

  // ═══════════════════════════════════════
  // WEBHOOKS
  // ═══════════════════════════════════════
  { key: 'webhooks.max', label: 'Máx. webhooks', category: 'webhooks', defaultLimitType: 'number', description: 'Limite de webhooks configurados', addonPrice: null },
  { key: 'webhooks.executions_daily', label: 'Execuções diárias', category: 'webhooks', defaultLimitType: 'number', description: 'Limite de execuções de webhook por dia', addonPrice: null },

  // ═══════════════════════════════════════
  // CHAMADAS (WhatsApp Calling)
  // ═══════════════════════════════════════
  { key: 'calling.whatsapp', label: 'Chamadas WhatsApp', category: 'calling', defaultLimitType: 'boolean', description: 'Habilitar chamadas de voz WhatsApp', addonPrice: 69.90 },
  { key: 'calling.ai_voice', label: 'Agente de voz IA', category: 'calling', defaultLimitType: 'boolean', description: 'Atendimento automático por IA em chamadas', addonPrice: 99.90 },
  { key: 'calling.transcription', label: 'Transcrição e Análise', category: 'calling', defaultLimitType: 'boolean', description: 'Transcrição automática e análise AIDA de chamadas', addonPrice: 1502 },
  { key: 'calling.minutes_monthly', label: 'Minutos mensais', category: 'calling', defaultLimitType: 'number', description: 'Limite de minutos de chamada por mês', addonPrice: null },

  // ═══════════════════════════════════════
  // SALES INTELLIGENCE
  // ═══════════════════════════════════════
  { key: 'sales.enabled', label: 'Sales Intelligence', category: 'sales', defaultLimitType: 'boolean', description: 'Habilitar detecção inteligente de oportunidades de venda', addonPrice: 59.90 },
  { key: 'sales.suggestions_monthly', label: 'Sugestões mensais', category: 'sales', defaultLimitType: 'number', description: 'Limite de sugestões de venda geradas por mês', addonPrice: null },
  { key: 'sales.rules_max', label: 'Máx. regras', category: 'sales', defaultLimitType: 'number', description: 'Limite de regras de detecção configuráveis', addonPrice: null },

  // ══════════════════════════════════════
  // CRM / KANBAN (página /crm)
  // ══════════════════════════════════════
  // Controla o acesso à página /crm (Kanban). Independe do provider:
  // Advbox/Projuris/Pipedrive/Internal são detalhes de renderização.
  { key: 'crm.enabled', label: 'CRM / Kanban', category: 'crm', defaultLimitType: 'boolean', description: 'Acesso à página de Kanban / CRM (multi-provider)', addonPrice: 49.90 },

  // ══════════════════════════════════════
  // ADS INTELLIGENCE (aba /relatorios/ads)
  // ══════════════════════════════════════
  // Incluído no Enterprise; add-on para os demais planos. Preço a definir (placeholder).
  { key: 'ads.enabled', label: 'Ads Intelligence', category: 'ads', defaultLimitType: 'boolean', description: 'Análise de anúncios Meta (gasto, CPL, ROAS) cruzada com resultados de venda', addonPrice: 99.90 },
];

/**
 * Mapa rápido key → metadata
 */
export const FEATURE_MAP = Object.fromEntries(
  FEATURE_KEYS.map(f => [f.key, f])
);

/**
 * Features agrupadas por categoria (para renderização)
 */
export function getFeaturesByCategory() {
  const grouped = {};
  for (const feature of FEATURE_KEYS) {
    if (!grouped[feature.category]) {
      grouped[feature.category] = {
        ...FEATURE_CATEGORIES[feature.category],
        features: [],
      };
    }
    grouped[feature.category].features.push(feature);
  }

  return Object.entries(grouped)
    .sort(([, a], [, b]) => a.order - b.order)
    .map(([key, value]) => ({ key, ...value }));
}

/**
 * Retorna todas as feature keys como array de strings
 */
export function getAllFeatureKeys() {
  return FEATURE_KEYS.map(f => f.key);
}

/**
 * Garante a compatibilidade de planos criados antes de contact.max e faz todo
 * plano novo nascer ilimitado quando não houver uma escolha explícita.
 */
export function withDefaultContactLimit(features = []) {
  if (features.some((feature) => feature.featureKey === CONTACT_LIMIT_FEATURE_KEY)) {
    return features;
  }

  return [
    ...features,
    {
      featureKey: CONTACT_LIMIT_FEATURE_KEY,
      limitType: 'unlimited',
      limitValue: null,
      addonPrice: null,
      config: null,
    },
  ];
}

/**
 * Valores padrão das features para cada tier de plano
 * Usado no seed e na criação de novos planos
 */
export const PLAN_DEFAULTS = {
  starter: {
    // Users
    'users.max': { limitType: 'number', limitValue: 3 },
    'users.agents_max': { limitType: 'number', limitValue: 3 },
    [CONTACT_LIMIT_FEATURE_KEY]: { limitType: 'unlimited', limitValue: null },
    // Channels
    'channels.whatsapp': { limitType: 'boolean', limitValue: 1 },
    'channels.whatsapp_numbers': { limitType: 'number', limitValue: 1 },
    [APP_CONNECT_FEATURE_KEY]: { limitType: 'boolean', limitValue: 0 },
    'channels.instagram': { limitType: 'boolean', limitValue: 0 },
    'channels.instagram_accounts': { limitType: 'number', limitValue: 0 },
    'channels.tiktok': { limitType: 'boolean', limitValue: 0 },
    'channels.tiktok_accounts': { limitType: 'number', limitValue: 0 },
    'channels.email': { limitType: 'boolean', limitValue: 0 },
    'channels.email_accounts': { limitType: 'number', limitValue: 0 },
    'channels.telegram': { limitType: 'boolean', limitValue: 0 },
    'channels.telegram_accounts': { limitType: 'number', limitValue: 0 },
    'channels.webchat': { limitType: 'boolean', limitValue: 1 },
    'channels.webchat_accounts': { limitType: 'number', limitValue: 1 },
    // Inbox
    'inbox.enabled': { limitType: 'boolean', limitValue: 1 },
    'inbox.tickets_monthly': { limitType: 'number', limitValue: 500 },
    'inbox.auto_distribution': { limitType: 'boolean', limitValue: 0 },
    'inbox.sla': { limitType: 'boolean', limitValue: 0 },
    'inbox.groups': { limitType: 'boolean', limitValue: 0 },
    'inbox.calls': { limitType: 'boolean', limitValue: 0 },
    'inbox.csat': { limitType: 'boolean', limitValue: 0 },
    'inbox.nps': { limitType: 'boolean', limitValue: 0 },
    // Flows
    'flows.max': { limitType: 'number', limitValue: 5 },
    'flows.nodes_per_flow': { limitType: 'number', limitValue: 50 },
    'flows.versions': { limitType: 'number', limitValue: 10 },
    // AI
    'ai.enabled': { limitType: 'boolean', limitValue: 1 },
    'ai.agent_calls': { limitType: 'number', limitValue: 500 },
    'ai.tokens_monthly': { limitType: 'number', limitValue: 100000 },
    'ai.classifier': { limitType: 'boolean', limitValue: 1 },
    'ai.extractor': { limitType: 'boolean', limitValue: 1 },
    'ai.summarizer': { limitType: 'boolean', limitValue: 0 },
    'ai.premium_models': { limitType: 'boolean', limitValue: 0 },
    // Integrations
    'integrations.google_calendar': { limitType: 'boolean', limitValue: 1 },
    'integrations.google_mail': { limitType: 'boolean', limitValue: 0 },
    'integrations.asaas': { limitType: 'boolean', limitValue: 0 },
    'integrations.advbox': { limitType: 'boolean', limitValue: 0 },
    'integrations.projuris': { limitType: 'boolean', limitValue: 0 },
    'integrations.pipedrive': { limitType: 'boolean', limitValue: 0 },
    'integrations.rdstation': { limitType: 'boolean', limitValue: 0 },
    'integrations.trello': { limitType: 'boolean', limitValue: 0 },
    'integrations.faz': { limitType: 'boolean', limitValue: 0 },
    'integrations.controljus': { limitType: 'boolean', limitValue: 0 },
    'embed.partner': { limitType: 'boolean', limitValue: 0 },
    // Reports
    'reports.basic': { limitType: 'boolean', limitValue: 1 },
    'reports.advanced': { limitType: 'boolean', limitValue: 0 },
    'reports.overview': { limitType: 'boolean', limitValue: 1 },
    'reports.agents': { limitType: 'boolean', limitValue: 0 },
    'reports.channels': { limitType: 'boolean', limitValue: 0 },
    'reports.satisfaction': { limitType: 'boolean', limitValue: 0 },
    'reports.sla': { limitType: 'boolean', limitValue: 0 },
    'reports.insights': { limitType: 'boolean', limitValue: 0 },
    'reports.custom': { limitType: 'boolean', limitValue: 0 },
    // Automations
    'automations.enabled': { limitType: 'boolean', limitValue: 0 },
    'automations.scheduled_jobs_max': { limitType: 'number', limitValue: 0 },
    'automations.executions_monthly': { limitType: 'number', limitValue: 0 },
    // RAG
    'rag.enabled': { limitType: 'boolean', limitValue: 0 },
    'rag.documents_max': { limitType: 'number', limitValue: 0 },
    // Webhooks
    'webhooks.max': { limitType: 'number', limitValue: 3 },
    'webhooks.executions_daily': { limitType: 'number', limitValue: 100 },
    // Calling
    'calling.whatsapp': { limitType: 'boolean', limitValue: 0 },
    'calling.ai_voice': { limitType: 'boolean', limitValue: 0 },
    'calling.transcription': { limitType: 'boolean', limitValue: 0 },
    'calling.minutes_monthly': { limitType: 'number', limitValue: 0 },
    // Sales Intelligence
    'sales.enabled': { limitType: 'boolean', limitValue: 0 },
    'sales.suggestions_monthly': { limitType: 'number', limitValue: 0 },
    'sales.rules_max': { limitType: 'number', limitValue: 0 },
    // CRM / Kanban
    'crm.enabled': { limitType: 'boolean', limitValue: 0 },
    // Ads Intelligence (só via add-on neste tier)
    'ads.enabled': { limitType: 'boolean', limitValue: 0 },
  },

  professional: {
    // Users
    'users.max': { limitType: 'number', limitValue: 10 },
    'users.agents_max': { limitType: 'number', limitValue: 10 },
    [CONTACT_LIMIT_FEATURE_KEY]: { limitType: 'unlimited', limitValue: null },
    // Channels
    'channels.whatsapp': { limitType: 'boolean', limitValue: 1 },
    'channels.whatsapp_numbers': { limitType: 'number', limitValue: 3 },
    [APP_CONNECT_FEATURE_KEY]: { limitType: 'boolean', limitValue: 0 },
    'channels.instagram': { limitType: 'boolean', limitValue: 1 },
    'channels.instagram_accounts': { limitType: 'number', limitValue: 1 },
    'channels.tiktok': { limitType: 'boolean', limitValue: 0 },
    'channels.tiktok_accounts': { limitType: 'number', limitValue: 0 },
    'channels.email': { limitType: 'boolean', limitValue: 1 },
    'channels.email_accounts': { limitType: 'number', limitValue: 2 },
    'channels.telegram': { limitType: 'boolean', limitValue: 1 },
    'channels.telegram_accounts': { limitType: 'number', limitValue: 1 },
    'channels.webchat': { limitType: 'boolean', limitValue: 1 },
    'channels.webchat_accounts': { limitType: 'number', limitValue: 2 },
    // Inbox
    'inbox.enabled': { limitType: 'boolean', limitValue: 1 },
    'inbox.tickets_monthly': { limitType: 'number', limitValue: 2000 },
    'inbox.auto_distribution': { limitType: 'boolean', limitValue: 1 },
    'inbox.sla': { limitType: 'boolean', limitValue: 1 },
    'inbox.groups': { limitType: 'boolean', limitValue: 1 },
    'inbox.calls': { limitType: 'boolean', limitValue: 0 },
    'inbox.csat': { limitType: 'boolean', limitValue: 1 },
    'inbox.nps': { limitType: 'boolean', limitValue: 0 },
    // Flows
    'flows.max': { limitType: 'number', limitValue: 20 },
    'flows.nodes_per_flow': { limitType: 'number', limitValue: 100 },
    'flows.versions': { limitType: 'number', limitValue: 50 },
    // AI
    'ai.enabled': { limitType: 'boolean', limitValue: 1 },
    'ai.agent_calls': { limitType: 'number', limitValue: 2000 },
    'ai.tokens_monthly': { limitType: 'number', limitValue: 500000 },
    'ai.classifier': { limitType: 'boolean', limitValue: 1 },
    'ai.extractor': { limitType: 'boolean', limitValue: 1 },
    'ai.summarizer': { limitType: 'boolean', limitValue: 1 },
    'ai.premium_models': { limitType: 'boolean', limitValue: 0 },
    // Integrations
    'integrations.google_calendar': { limitType: 'boolean', limitValue: 1 },
    'integrations.google_mail': { limitType: 'boolean', limitValue: 1 },
    'integrations.asaas': { limitType: 'boolean', limitValue: 1 },
    'integrations.advbox': { limitType: 'boolean', limitValue: 1 },
    'integrations.projuris': { limitType: 'boolean', limitValue: 1 },
    'integrations.pipedrive': { limitType: 'boolean', limitValue: 1 },
    'integrations.rdstation': { limitType: 'boolean', limitValue: 1 },
    'integrations.trello': { limitType: 'boolean', limitValue: 1 },
    'integrations.faz': { limitType: 'boolean', limitValue: 1 },
    'integrations.controljus': { limitType: 'boolean', limitValue: 1 },
    'embed.partner': { limitType: 'boolean', limitValue: 0 },
    // Reports
    'reports.basic': { limitType: 'boolean', limitValue: 1 },
    'reports.advanced': { limitType: 'boolean', limitValue: 1 },
    'reports.overview': { limitType: 'boolean', limitValue: 1 },
    'reports.agents': { limitType: 'boolean', limitValue: 1 },
    'reports.channels': { limitType: 'boolean', limitValue: 1 },
    'reports.satisfaction': { limitType: 'boolean', limitValue: 1 },
    'reports.sla': { limitType: 'boolean', limitValue: 0 },
    'reports.insights': { limitType: 'boolean', limitValue: 1 },
    'reports.custom': { limitType: 'boolean', limitValue: 1 },
    // Automations
    'automations.enabled': { limitType: 'boolean', limitValue: 1 },
    'automations.scheduled_jobs_max': { limitType: 'number', limitValue: 5 },
    'automations.executions_monthly': { limitType: 'number', limitValue: 1000 },
    // RAG
    'rag.enabled': { limitType: 'boolean', limitValue: 1 },
    'rag.documents_max': { limitType: 'number', limitValue: 50 },
    // Webhooks
    'webhooks.max': { limitType: 'number', limitValue: 10 },
    'webhooks.executions_daily': { limitType: 'number', limitValue: 500 },
    // Calling
    'calling.whatsapp': { limitType: 'boolean', limitValue: 0 },
    'calling.ai_voice': { limitType: 'boolean', limitValue: 0 },
    'calling.transcription': { limitType: 'boolean', limitValue: 0 },
    'calling.minutes_monthly': { limitType: 'number', limitValue: 0 },
    // Sales Intelligence
    'sales.enabled': { limitType: 'boolean', limitValue: 1 },
    'sales.suggestions_monthly': { limitType: 'number', limitValue: 200 },
    'sales.rules_max': { limitType: 'number', limitValue: 10 },
    // CRM / Kanban
    'crm.enabled': { limitType: 'boolean', limitValue: 1 },
    // Ads Intelligence (só via add-on neste tier)
    'ads.enabled': { limitType: 'boolean', limitValue: 0 },
  },

  enterprise: {
    // Users
    'users.max': { limitType: 'unlimited', limitValue: null },
    'users.agents_max': { limitType: 'unlimited', limitValue: null },
    [CONTACT_LIMIT_FEATURE_KEY]: { limitType: 'unlimited', limitValue: null },
    // Channels
    'channels.whatsapp': { limitType: 'boolean', limitValue: 1 },
    'channels.whatsapp_numbers': { limitType: 'unlimited', limitValue: null },
    [APP_CONNECT_FEATURE_KEY]: { limitType: 'boolean', limitValue: 0 },
    'channels.instagram': { limitType: 'boolean', limitValue: 1 },
    'channels.instagram_accounts': { limitType: 'unlimited', limitValue: null },
    'channels.tiktok': { limitType: 'boolean', limitValue: 1 },
    'channels.tiktok_accounts': { limitType: 'unlimited', limitValue: null },
    'channels.email': { limitType: 'boolean', limitValue: 1 },
    'channels.email_accounts': { limitType: 'unlimited', limitValue: null },
    'channels.telegram': { limitType: 'boolean', limitValue: 1 },
    'channels.telegram_accounts': { limitType: 'unlimited', limitValue: null },
    'channels.webchat': { limitType: 'boolean', limitValue: 1 },
    'channels.webchat_accounts': { limitType: 'unlimited', limitValue: null },
    // Inbox
    'inbox.enabled': { limitType: 'boolean', limitValue: 1 },
    'inbox.tickets_monthly': { limitType: 'unlimited', limitValue: null },
    'inbox.auto_distribution': { limitType: 'boolean', limitValue: 1 },
    'inbox.sla': { limitType: 'boolean', limitValue: 1 },
    'inbox.groups': { limitType: 'boolean', limitValue: 1 },
    'inbox.calls': { limitType: 'boolean', limitValue: 1 },
    'inbox.csat': { limitType: 'boolean', limitValue: 1 },
    'inbox.nps': { limitType: 'boolean', limitValue: 1 },
    // Flows
    'flows.max': { limitType: 'unlimited', limitValue: null },
    'flows.nodes_per_flow': { limitType: 'unlimited', limitValue: null },
    'flows.versions': { limitType: 'unlimited', limitValue: null },
    // AI
    'ai.enabled': { limitType: 'boolean', limitValue: 1 },
    'ai.agent_calls': { limitType: 'unlimited', limitValue: null },
    'ai.tokens_monthly': { limitType: 'unlimited', limitValue: null },
    'ai.classifier': { limitType: 'boolean', limitValue: 1 },
    'ai.extractor': { limitType: 'boolean', limitValue: 1 },
    'ai.summarizer': { limitType: 'boolean', limitValue: 1 },
    'ai.premium_models': { limitType: 'boolean', limitValue: 1 },
    // Integrations
    'integrations.google_calendar': { limitType: 'boolean', limitValue: 1 },
    'integrations.google_mail': { limitType: 'boolean', limitValue: 1 },
    'integrations.asaas': { limitType: 'boolean', limitValue: 1 },
    'integrations.advbox': { limitType: 'boolean', limitValue: 1 },
    'integrations.projuris': { limitType: 'boolean', limitValue: 1 },
    'integrations.pipedrive': { limitType: 'boolean', limitValue: 1 },
    'integrations.rdstation': { limitType: 'boolean', limitValue: 1 },
    'integrations.trello': { limitType: 'boolean', limitValue: 1 },
    'integrations.faz': { limitType: 'boolean', limitValue: 1 },
    'integrations.controljus': { limitType: 'boolean', limitValue: 1 },
    'embed.partner': { limitType: 'boolean', limitValue: 0 },
    // Reports
    'reports.basic': { limitType: 'boolean', limitValue: 1 },
    'reports.advanced': { limitType: 'boolean', limitValue: 1 },
    'reports.overview': { limitType: 'boolean', limitValue: 1 },
    'reports.agents': { limitType: 'boolean', limitValue: 1 },
    'reports.channels': { limitType: 'boolean', limitValue: 1 },
    'reports.satisfaction': { limitType: 'boolean', limitValue: 1 },
    'reports.sla': { limitType: 'boolean', limitValue: 1 },
    'reports.insights': { limitType: 'boolean', limitValue: 1 },
    'reports.custom': { limitType: 'boolean', limitValue: 1 },
    // Automations
    'automations.enabled': { limitType: 'boolean', limitValue: 1 },
    'automations.scheduled_jobs_max': { limitType: 'unlimited', limitValue: null },
    'automations.executions_monthly': { limitType: 'unlimited', limitValue: null },
    // RAG
    'rag.enabled': { limitType: 'boolean', limitValue: 1 },
    'rag.documents_max': { limitType: 'number', limitValue: 500 },
    // Webhooks
    'webhooks.max': { limitType: 'unlimited', limitValue: null },
    'webhooks.executions_daily': { limitType: 'unlimited', limitValue: null },
    // Calling
    'calling.whatsapp': { limitType: 'boolean', limitValue: 1 },
    'calling.ai_voice': { limitType: 'boolean', limitValue: 1 },
    'calling.transcription': { limitType: 'boolean', limitValue: 0 },
    'calling.minutes_monthly': { limitType: 'number', limitValue: 1000 },
    // Sales Intelligence
    'sales.enabled': { limitType: 'boolean', limitValue: 1 },
    'sales.suggestions_monthly': { limitType: 'unlimited', limitValue: null },
    'sales.rules_max': { limitType: 'unlimited', limitValue: null },
    // CRM / Kanban
    'crm.enabled': { limitType: 'boolean', limitValue: 1 },
    // Ads Intelligence (incluído no Enterprise)
    'ads.enabled': { limitType: 'boolean', limitValue: 1 },
  },
};
